import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, fmtErr } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { fmtTRY, fmtGold, fmtDate } from "../lib/format";
import { Copy, ExternalLink, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const [ev, setEv] = useState(null);
  const [wall, setWall] = useState({ totals: { cash_kurus: 0, gold_mg: 0, count: 0 }, gifts: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/events/${id}`);
        setEv(data);
        const w = await http.get(`/public/events/${data.invite_key}/wall`);
        setWall(w.data);
      } catch (e) { toast.error(fmtErr(e)); }
    })();
  }, [id]);

  const guestUrl = ev ? `${window.location.origin}/g/${ev.invite_key}` : "";
  const copyLink = () => { navigator.clipboard.writeText(guestUrl); toast.success("Bağlantı kopyalandı"); };
  const qrUrl = ev ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(guestUrl)}` : "";

  if (!ev) return <div className="mx-auto max-w-7xl px-4 py-10 text-muted-foreground">Yükleniyor...</div>;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-primary/80">{t.types?.[ev.event_type] || ev.event_type}</div>
          <h1 className="font-serif text-4xl mt-1" data-testid="event-title">{ev.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{fmtDate(ev.starts_at)} · {ev.location}</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.total_cash}</div>
            <div className="font-mono text-2xl mt-2" data-testid="stat-cash">{fmtTRY(wall.totals.cash_kurus)}</div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.total_gold}</div>
            <div className="font-mono text-2xl mt-2" data-testid="stat-gold">{fmtGold(wall.totals.gold_mg)}</div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.gift_count}</div>
            <div className="font-mono text-2xl mt-2" data-testid="stat-count">{wall.totals.count}</div>
          </Card>
        </div>

        <Card className="p-6">
          <h2 className="font-serif text-2xl mb-4">{t.wall}</h2>
          {wall.gifts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Henüz hediye yok.</p>
          ) : (
            <div className="space-y-3">
              {wall.gifts.map((g, i) => (
                <div key={g.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 animate-float-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`wall-item-${i}`}>
                  <div>
                    <div className="font-serif text-lg">{g.display_name}</div>
                    {g.message ? <div className="text-xs text-muted-foreground mt-0.5">"{g.message}"</div> : null}
                  </div>
                  <div className="text-end">
                    <div className="text-xs uppercase tracking-wider text-primary/70">{g.kind === "gold" ? t.gold : t.cash}</div>
                    <div className="font-mono text-sm">{g.kind === "gold" ? fmtGold(g.grams_mg) : fmtTRY(g.amount_kurus)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="lg:col-span-4 space-y-6">
        <Card className="p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t.invite_key}</div>
          <div className="font-mono text-xl gold-text" data-testid="invite-key">{ev.invite_key}</div>
          <div className="mt-4 aspect-square rounded-lg bg-white p-2">
            <img alt="qr" src={qrUrl} className="w-full h-full object-contain" />
          </div>
          <div className="mt-4 space-y-2">
            <Button className="w-full gap-2 rounded-full" onClick={copyLink} data-testid="copy-link"><Copy className="w-4 h-4" /> Kopyala</Button>
            <Button asChild variant="outline" className="w-full gap-2 rounded-full" data-testid="open-guest">
              <Link to={`/g/${ev.invite_key}`} target="_blank"><ExternalLink className="w-4 h-4" /> {t.guests_link}</Link>
            </Button>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-2 text-primary mb-2"><KeyRound className="w-4 h-4" /> <span className="text-xs uppercase tracking-wider">{t.event_password}</span></div>
          <p className="text-sm text-muted-foreground">Parolayı yalnızca davet ettiğiniz kişilerle paylaşın. Misafirler önce OTP, ardından parola ile giriş yapar.</p>
        </Card>
      </div>
    </div>
  );
}
